/**
 * Pure, test-friendly tennis scoring state machine.
 *
 * Sprint 2 scope: a single game with classic 15/30/40/AD scoring, plus a
 * running list of completed games. No sets or tiebreaks yet — when scoring
 * evolves, extend the public API here rather than spreading the logic.
 */

export type PlayerId = 'p1' | 'p2';

export interface CompletedGame {
  readonly p1: number;
  readonly p2: number;
  readonly winner: PlayerId;
}

export interface MatchState {
  /** Raw points in the current game (0, 1, 2, 3, 4...). */
  readonly p1Points: number;
  readonly p2Points: number;
  /** Completed games in order. */
  readonly games: readonly CompletedGame[];
  /** Point-by-point history (for undo). */
  readonly history: readonly PlayerId[];
}

export function emptyMatchState(): MatchState {
  return { p1Points: 0, p2Points: 0, games: [], history: [] };
}

/** Render raw points as the tennis-scoring label for the *current* game. */
export function pointLabel(points: number, otherPoints: number): string {
  // Deuce territory: both players at 3+.
  if (points >= 3 && otherPoints >= 3) {
    if (points === otherPoints) return '40';
    if (points === otherPoints + 1) return 'AD';
    // Trailing during advantage — show 40.
    return '40';
  }
  if (points === 0) return '0';
  if (points === 1) return '15';
  if (points === 2) return '30';
  return '40';
}

interface AwardOutcome {
  readonly state: MatchState;
  readonly gameWon: PlayerId | null;
}

/**
 * Award a point to `p`. Returns the new state and, if this ended a game,
 * the winner's id (also appended to `state.games`).
 */
export function awardPoint(state: MatchState, p: PlayerId): AwardOutcome {
  const p1 = state.p1Points + (p === 'p1' ? 1 : 0);
  const p2 = state.p2Points + (p === 'p2' ? 1 : 0);
  const history = [...state.history, p];

  const minWin = 4;
  const margin = 2;
  const leader = p1 >= minWin && p1 - p2 >= margin ? 'p1' : p2 >= minWin && p2 - p1 >= margin ? 'p2' : null;

  if (leader) {
    return {
      state: {
        p1Points: 0,
        p2Points: 0,
        games: [...state.games, { p1, p2, winner: leader }],
        history,
      },
      gameWon: leader,
    };
  }
  return {
    state: { ...state, p1Points: p1, p2Points: p2, history },
    gameWon: null,
  };
}

/** Undo the most recent point. Rebuilds from history — O(n) but n is small. */
export function undoPoint(state: MatchState): MatchState {
  if (state.history.length === 0) return state;
  const next = state.history.slice(0, -1);
  let rebuilt = emptyMatchState();
  for (const p of next) {
    rebuilt = awardPoint(rebuilt, p).state;
  }
  return rebuilt;
}

export function resetMatch(): MatchState {
  return emptyMatchState();
}
